import {createSlice, Dispatch} from '@reduxjs/toolkit';
import type {AxiosError} from 'axios';
import {AgentService} from "app/services";
import {FilterAgentsQueryParams, PagedAgentList} from 'app/types/service-types/AgentTypes';
import {errorMsg} from "../../../utils/MessageUtils";

const getErrorMessage = (e: unknown, fallback: string) => {
  const err = e as AxiosError<any>;
  return (
    (err?.response?.data?.message as string) ||
    (err as any)?.data?.message ||
    err?.message ||
    fallback
  );
};

export const fetchAllAgents = (queryParams?: FilterAgentsQueryParams) => async (dispatch: Dispatch) => {
  try {
    dispatch(setLoading(true));
    let response;
    if (queryParams && Object.keys(queryParams).length > 0) {
      response = await AgentService.filterAgents(queryParams);
    } else {
      response = await AgentService.fetchAll();
    }
    dispatch(setAgentsList(response));
  } catch (e) {
    dispatch(errorMsg(getErrorMessage(e, 'Failed to load agents')));
  } finally {
    dispatch(setLoading(false));
  }
}

/**
 * ROLE_AGENT view: load ONLY sub-agents (not the full agents list).
 * Uses backend endpoints:
 *   - GET /api/sub-agents
 *   - GET /api/sub-agents?{queryParams}
 */
export const fetchAllSubAgents = (queryParams?: FilterAgentsQueryParams) => async (dispatch: Dispatch) => {
  try {
    dispatch(setLoading(true));
    let response;
    if (queryParams && Object.keys(queryParams).length > 0) {
      response = await AgentService.filterSubAgents(queryParams);
    } else {
      response = await AgentService.fetchSubAgents();
    }

    // Some backends return a plain array; normalize to the paged shape used by AgentsTable.
    const normalized = Array.isArray(response)
      ? {content: response, totalPages: 1, totalElements: response.length, number: 0, size: response.length}
      : response;

    dispatch(setAgentsList(normalized));
  } catch (e) {
    dispatch(errorMsg(getErrorMessage(e, 'Failed to load sub-agents')));
  } finally {
    dispatch(setLoading(false));
  }
}

interface AgentsListSlice {
  allAgents: PagedAgentList | null;
  loading: boolean;
}

const initialState: AgentsListSlice = {
  allAgents: null,
  loading: false
}

const stateSlice = createSlice({
  name: 'agent/agent-list',
  initialState,
  reducers: {
    setAgentsList: (state, action) => {
      state.allAgents = action.payload
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    }
  }
});

export const {setAgentsList, setLoading} = stateSlice.actions;

export default stateSlice.reducer;